import { useCallback, useEffect, useState } from 'react';
import { PHONE_TILT } from '@erectus/shared';
import { currentPitch, currentTilt, startTiltWatch, stopTiltWatch } from '@/lib/motion.js';

export interface PhoneTilt {
  /** Sideways roll, degrees (corrected in measurement up to PHONE_TILT.maxRollDeg). */
  roll: number;
  /** Leaning back or forward, degrees (recorded, not corrected). */
  pitch: number;
}

/**
 * Live phone tilt for a capture screen: null until the first reading, and
 * stays null on devices without a motion sensor (the checklist row is then
 * left out rather than failing). `enable` is for a tap, where a browser that
 * still needs permission can ask.
 */
export function useTilt(): { tilt: PhoneTilt | null; enable(): void } {
  const [tilt, setTilt] = useState<PhoneTilt | null>(null);

  useEffect(() => {
    void startTiltWatch();
    const timer = window.setInterval(() => {
      const roll = currentTilt();
      const pitch = currentPitch();
      setTilt(roll === null || pitch === null ? null : { roll, pitch });
    }, 400);
    return () => {
      window.clearInterval(timer);
      stopTiltWatch();
    };
  }, []);

  const enable = useCallback(() => void startTiltWatch(), []);
  return { tilt, enable };
}

/** The checklist row: generous, because roll is corrected and pitch only recorded. */
export function phoneRow(tilt: PhoneTilt): { id: 'phone_upright'; label: string; passed: boolean } {
  return {
    id: 'phone_upright',
    label: 'Phone roughly upright',
    passed: tilt.roll <= PHONE_TILT.maxRollDeg && tilt.pitch <= PHONE_TILT.maxPitchDeg,
  };
}
